import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CalendarOff } from 'lucide-react';
import type {
  DepreciationScheduleClassRow,
  DepreciationScheduleAssetRow,
  GroupPolicyTier,
} from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import type { ColumnDef } from '@tanstack/react-table';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd } from '../../lib/format';
import { FinancialsReportScreen } from '../../components/financials/FinancialsReportScreen';
import { CalculationBasisTrigger } from '../../components/financials/CalculationBasisDrawer';
import { qk } from '../../lib/queryKeys';

/**
 * Depreciation schedule report (SIL-250 T14, spec §4.2 / §7).
 *
 * GET /api/client/financials/depreciation — class-aggregated by default;
 * per-asset drill-down via ?assetClassId=&cursor=&limit= (spec §6). A class
 * whose resolved policy tier is `no_policy` renders a "no depreciation
 * policy" empty state instead of a chart — there is nothing to walk forward.
 */
export const Route = createFileRoute('/financials/depreciation')({
  component: DepreciationSchedulePage,
});

const POLICY_TIER_LABEL: Record<GroupPolicyTier, string> = {
  class_override: 'Class override',
  org_default: 'Org default',
  platform_default: 'Platform default',
  no_policy: 'No policy',
  // Epic 3 (lots-first-class) — only ever appears for a lot/team/site group
  // whose member assets resolved to more than one distinct tier. This page
  // stays class-scoped today (no Group by control), so this is unreachable
  // in practice, but the type is shared with the group-by surfaces.
  mixed: 'Mixed policies',
};

function PolicyTierBadge({ tier }: { tier: GroupPolicyTier }) {
  const styles: Record<GroupPolicyTier, string> = {
    class_override: 'bg-brand-50 text-brand-700 ring-brand-200',
    org_default: 'bg-sky-50 text-sky-700 ring-sky-200',
    platform_default: 'bg-muted text-muted-foreground ring-border',
    no_policy: 'bg-amber-50 text-amber-700 ring-amber-200',
    mixed: 'bg-violet-50 text-violet-700 ring-violet-200',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${styles[tier]}`}
    >
      {POLICY_TIER_LABEL[tier]}
    </span>
  );
}

function DepreciationSchedulePage() {
  const classesQ = useQuery({
    queryKey: qk.financials.depreciation(),
    queryFn: async () => {
      const res = await api.api.client.financials.depreciation.$get({ query: {} });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!('classes' in data)) throw new Error('unexpected depreciation response');
      return data;
    },
  });

  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  return (
    <FinancialsReportScreen
      title="Depreciation Schedule"
      pinTo="/financials/depreciation"
      subtitle="Month-by-month book value to the end of useful life, by asset class."
      query={classesQ}
      exportCsv={{
        path: '/api/client/financials/export/depreciation.csv',
        filename: 'financials-depreciation-schedule.csv',
      }}
      empty={(data) =>
        data.classes.length === 0
          ? {
              icon: CalendarOff,
              title: 'No asset classes yet',
              body: 'Once assets are enrolled, their depreciation schedules will appear here.',
            }
          : null
      }
    >
      {(data) => {
        const classes = data.classes;
        const selected: DepreciationScheduleClassRow | undefined =
          classes.find((c) => c.assetClassId === selectedClassId) ?? classes[0];

        return (
          <>
            {/* Class tabs */}
            <div className="flex flex-wrap gap-2">
              {classes.map((c) => {
                const active = c.assetClassId === (selected?.assetClassId ?? classes[0]?.assetClassId);
                return (
                  <button
                    key={c.assetClassId}
                    type="button"
                    onClick={() => setSelectedClassId(c.assetClassId)}
                    className={
                      active
                        ? 'rounded-full bg-brand-600 px-3 py-1.5 text-xs font-medium text-white'
                        : 'rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted'
                    }
                  >
                    {c.assetClassName}
                  </button>
                );
              })}
            </div>

            {selected && <ClassScheduleCard cls={selected} />}
          </>
        );
      }}
    </FinancialsReportScreen>
  );
}

/**
 * `policyTier === 'no_policy'` (and the Epic 3 `'mixed'` group case, which
 * this class-scoped page never actually reaches) are the ONLY states where
 * no capitalization policy resolved at all — the header's "No depreciation
 * method resolved" / badge text and the body's empty state must both key
 * off exactly this, never off `schedule.length` too.
 *
 * `schedule.length === 0` is NOT the same condition: a policy can resolve
 * (org default, platform default, whatever) while the forward walk still
 * can't run because the aggregate is missing an age anchor — e.g. no asset
 * in the class has both a gross book value and an `in_service_at` (see
 * `loadClassBookValueAggregate`'s `weightedAgeYears` doc comment in
 * `apps/api/src/lib/financials-report.ts`). Folding that into the same
 * "no policy" flag produced the prod contradiction: header named a real org
 * default (5 yr declining balance) while the body claimed no policy existed
 * at any tier. That's a missing-data state, not a missing-policy state, and
 * gets its own message below.
 */
export function policyState(cls: DepreciationScheduleClassRow): 'no_policy' | 'no_schedule_data' | 'ok' {
  if (cls.policyTier === 'no_policy' || cls.policyTier === 'mixed') return 'no_policy';
  if (cls.schedule.length === 0) return 'no_schedule_data';
  return 'ok';
}

export function ClassScheduleCard({ cls }: { cls: DepreciationScheduleClassRow }) {
  const state = policyState(cls);
  // This page stays class-scoped (no Group by control), so assetClassId/
  // assetClassName are always populated in practice — groupId/groupLabel
  // are the same values, just typed for every dimension.
  const label = cls.assetClassName ?? cls.groupLabel;
  const id = cls.assetClassId ?? cls.groupId ?? '';

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-foreground">
              {label}
              <CalculationBasisTrigger title={`${label} — Depreciation schedule`} basis={cls.basis} />
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {cls.method
                ? `${cls.method.replace('_', ' ')} · ${cls.usefulLifeYears ?? '—'} yr useful life`
                : 'No depreciation method resolved'}
            </p>
          </div>
          <PolicyTierBadge tier={cls.policyTier} />
        </div>

        {state === 'no_policy' && (
          <div className="mt-6 rounded-lg border border-dashed border-border bg-muted px-4 py-10 text-center">
            <CalendarOff className="mx-auto mb-3 size-7 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">No depreciation policy</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              This asset class has no resolved capitalization policy (no class override, org
              default, or platform default), so no schedule can be projected. Set a policy in
              Capitalization Policy settings to see one here.
            </p>
          </div>
        )}

        {state === 'no_schedule_data' && (
          <div className="mt-6 rounded-lg border border-dashed border-border bg-muted px-4 py-10 text-center">
            <CalendarOff className="mx-auto mb-3 size-7 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Schedule can&apos;t be projected yet</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              A depreciation policy is resolved for this class, but no schedule can be projected
              because its assets are missing the book value or in-service date data needed to
              anchor the forward walk.
            </p>
          </div>
        )}

        {state === 'ok' && (
          <div className="mt-5">
            <ScheduleChart schedule={cls.schedule} />
          </div>
        )}
      </Card>

      {state !== 'no_policy' && <AssetDrillDown key={id} assetClassId={id} />}
    </div>
  );
}

function ScheduleChart({
  schedule,
}: {
  schedule: DepreciationScheduleClassRow['schedule'];
}) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={schedule} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
        <XAxis
          dataKey="month"
          tickFormatter={(m: number) => `Mo ${m}`}
          stroke="var(--color-muted-foreground)"
          fontSize={12}
          tickLine={false}
          minTickGap={24}
        />
        <YAxis
          stroke="var(--color-muted-foreground)"
          fontSize={12}
          tickLine={false}
          tickFormatter={(v: number) => formatUsd(v, { compact: true })}
          width={56}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: 'var(--color-border)' }}
          labelFormatter={(m: number) => `Month ${m}`}
          formatter={(v: number, name: string) => [
            formatUsd(v),
            name === 'grossBookValueUsd'
              ? 'Gross book value'
              : name === 'accumulatedDepreciationUsd'
                ? 'Accumulated depreciation'
                : 'Net book value',
          ]}
        />
        <Line
          dataKey="grossBookValueUsd"
          stroke={SX_PALETTE[2] ?? SX_PALETTE[0]}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          dot={false}
        />
        <Line
          dataKey="accumulatedDepreciationUsd"
          stroke={SX_PALETTE[1] ?? SX_PALETTE[0]}
          strokeWidth={1.5}
          dot={false}
        />
        <Line dataKey="netBookValueUsd" stroke={SX_PALETTE[0]} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Per-asset drill-down — paginated (spec §6: unbounded month x asset arrays
// otherwise time out on large fleets). Collapsed by default.
// ---------------------------------------------------------------------------

function AssetDrillDown({ assetClassId }: { assetClassId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [rows, setRows] = useState<DepreciationScheduleAssetRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const pageQ = useQuery({
    queryKey: qk.financials.depreciationAssets(assetClassId, cursor),
    queryFn: async () => {
      const res = await api.api.client.financials.depreciation.$get({
        query: { assetClassId, limit: '25', ...(cursor ? { cursor } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const page = await res.json();
      if (!('assets' in page)) throw new Error('unexpected depreciation drill-down response');
      setRows((prev) => (cursor === null ? page.assets : [...prev, ...page.assets]));
      setNextCursor(page.pageMeta.nextCursor);
      return page;
    },
    enabled: expanded,
  });

  const columns: ColumnDef<DepreciationScheduleAssetRow, unknown>[] = useMemo(
    () => [
      {
        id: 'assetId',
        header: 'Asset',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.assetId.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'policyTier',
        header: 'Policy',
        cell: ({ row }) => <PolicyTierBadge tier={row.original.policyTier} />,
      },
      {
        id: 'currentNbv',
        header: () => <div className="text-right">Current NBV</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.schedule[0]?.netBookValueUsd ?? null} />
          </div>
        ),
      },
      {
        id: 'finalNbv',
        header: () => <div className="text-right">End-of-life NBV</div>,
        cell: ({ row }) => {
          const last = row.original.schedule[row.original.schedule.length - 1];
          return (
            <div className="text-right">
              <UsdCell value={last?.netBookValueUsd ?? null} />
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <Card>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="text-xs font-semibold text-brand-600 hover:underline"
      >
        {expanded ? 'Hide per-asset schedule' : 'Show per-asset schedule'}
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          <DataTable
            data={rows}
            columns={columns}
            emptyMessage={pageQ.isLoading ? 'Loading…' : 'No assets in this class.'}
          />
          {nextCursor && (
            <button
              type="button"
              onClick={() => setCursor(nextCursor)}
              disabled={pageQ.isFetching}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              {pageQ.isFetching ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
